import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, Chip, Button, Paper, Grid, TextField,
    Divider, Link, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';
import globals from '../globals';
import { useAuth } from '../context/AuthContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function renderWithLatex(text) {
    if (!text || !text.includes('$')) return text;
    return text.split('$').map((part, i) =>
        i % 2 === 1
            ? <InlineMath key={i} math={part} />
            : <React.Fragment key={i}>{part}</React.Fragment>
    );
}

function answersMatch(given, expected) {
    return String(given ?? '').trim().toLowerCase() === String(expected ?? '').trim().toLowerCase();
}

const STATUS_META = {
    not_started: { label: 'За оценяване', color: 'default' },
    in_progress: { label: 'Започнато', color: 'warning' },
    graded: { label: 'Оценено', color: 'success' },
};

function StatusChip({ status }) {
    const meta = STATUS_META[status] || STATUS_META.not_started;
    return <Chip size="small" label={meta.label} color={meta.color} />;
}

function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('bg-BG', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Suggested auto-score for a question from the student's answer.
function suggestPoints(question, answer) {
    if (!answer) return 0;
    const pts = question.points || 0;
    if (question.type === 'multiple_choice') {
        return answer.choice_id && String(answer.choice_id) === String(question.correct_option_id) ? pts : 0;
    }
    if (question.type === 'multi_part') {
        const subs = question.subquestions || [];
        if (subs.length === 0) return 0;
        const correct = subs.filter(sq => answersMatch(answer.sub_answers?.[sq.id], sq.correct_answer)).length;
        return Math.round((pts * correct) / subs.length);
    }
    return 0; // file_upload — manual
}

// Multiple-choice questions are graded automatically and locked; file uploads and
// multi-part (typed) answers are graded manually by the instructor.
function isAutoGraded(question) {
    return question.type === 'multiple_choice';
}

// ---------------------------------------------------------------------------
// Answer rendering (per question type)
// ---------------------------------------------------------------------------

function StudentAnswer({ question, answer }) {
    if (!answer) {
        return <Typography sx={{ color: globals.colors.gray, fontStyle: 'italic' }}>Без отговор</Typography>;
    }
    if (question.type === 'multiple_choice') {
        const chosen = (question.options || []).find(o => String(o.id) === String(answer.choice_id));
        const correct = (question.options || []).find(o => String(o.id) === String(question.correct_option_id));
        const isCorrect = chosen && correct && String(chosen.id) === String(correct.id);
        return (
            <Box>
                <Typography sx={{ color: isCorrect ? globals.colors.darkGreen : globals.colors.darkRed }}>
                    Отговор на ученика: {chosen ? renderWithLatex(chosen.text) : '—'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: globals.colors.gray }}>
                    Верен отговор: {correct ? renderWithLatex(correct.text) : '—'}
                </Typography>
            </Box>
        );
    }
    if (question.type === 'multi_part') {
        return (
            <Box>
                {(question.subquestions || []).map((sq, i) => {
                    const given = answer.sub_answers?.[sq.id] ?? '';
                    const ok = answersMatch(given, sq.correct_answer);
                    return (
                        <Box key={sq.id} sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: 14 }}>
                                {i + 1}. {renderWithLatex(sq.text)}
                            </Typography>
                            <Typography sx={{ fontSize: 14, color: ok ? globals.colors.darkGreen : globals.colors.darkRed }}>
                                Отговор: {given || '—'} · Верен: {sq.correct_answer}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        );
    }
    // file_upload
    const files = answer.files || [];
    if (files.length === 0) {
        return <Typography sx={{ color: globals.colors.gray, fontStyle: 'italic' }}>Няма качени файлове</Typography>;
    }
    return (
        <Box>
            {files.map((f, i) => (
                <Link key={i} href={f.url} target="_blank" rel="noopener" sx={{ display: 'block', fontSize: 14 }}>
                    {f.name || `Файл ${i + 1}`}
                </Link>
            ))}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Grading view for a single submission
// ---------------------------------------------------------------------------

function GradingView({ submissionId, token, onBack }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [grades, setGrades] = useState({}); // qid -> { points, feedback }
    const [feedback, setFeedback] = useState('');
    const [saving, setSaving] = useState(false);
    const [savedMsg, setSavedMsg] = useState('');

    useEffect(() => {
        setLoading(true);
        fetch(`${globals.apiUrl}/exams/submissions/${submissionId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(data => {
                setDetail(data);
                setFeedback(data.feedback || '');
                // Seed grades from existing, else auto-suggest.
                const existing = data.grades || {};
                const seeded = {};
                (data.sections || []).forEach(s => (s.questions || []).forEach(q => {
                    const prior = existing[String(q.id)];
                    const auto = suggestPoints(q, (data.answers || {})[String(q.id)]);
                    if (isAutoGraded(q)) {
                        // Locked: always reflect the computed score, keep any prior comment.
                        seeded[q.id] = { points: auto, feedback: prior?.feedback || '' };
                    } else if (prior) {
                        seeded[q.id] = { points: prior.points ?? auto, feedback: prior.feedback || '' };
                    } else {
                        seeded[q.id] = { points: auto, feedback: '' };
                    }
                }));
                setGrades(seeded);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [submissionId, token]);

    const setPoints = (qid, max, val) => {
        let n = Number(val);
        if (Number.isNaN(n)) n = 0;
        n = Math.max(0, Math.min(n, max));
        setGrades(prev => ({ ...prev, [qid]: { ...prev[qid], points: n } }));
    };
    const setQFeedback = (qid, val) => {
        setGrades(prev => ({ ...prev, [qid]: { ...prev[qid], feedback: val } }));
    };

    const totalPoints = Object.values(grades).reduce((sum, g) => sum + (Number(g.points) || 0), 0);

    const save = async (finish) => {
        setSaving(true);
        setSavedMsg('');
        try {
            const res = await fetch(`${globals.apiUrl}/exams/submissions/${submissionId}/grade`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ grades, feedback, finish }),
            });
            if (res.ok) {
                if (finish) {
                    onBack(true);
                    return;
                }
                setSavedMsg('Черновата е запазена.');
            } else {
                setSavedMsg('Грешка при запазване.');
            }
        } catch {
            setSavedMsg('Грешка при запазване.');
        }
        setSaving(false);
    };

    if (loading) {
        return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    }
    if (!detail) {
        return <Typography color="error">Грешка при зареждане на отговорите.</Typography>;
    }

    return (
        <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={() => onBack(false)} sx={{ mb: 2 }}>
                Назад към предаванията
            </Button>

            <Paper elevation={2} sx={{ p: 2, mb: 2, backgroundColor: globals.colors.lightPink }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: 18, color: globals.colors.darkBlue }}>
                    {detail.exam_title}
                </Typography>
                <Typography sx={{ fontSize: 14 }}>
                    {detail.student_name} · {detail.student_email}
                </Typography>
                <Typography sx={{ fontSize: 13, color: globals.colors.gray }}>
                    Предаден на {formatDate(detail.submitted_at)}
                </Typography>
                <Typography sx={{ mt: 1, fontWeight: 'bold', color: globals.colors.lightRed }}>
                    Общо: {totalPoints} / {detail.max_points} точки
                </Typography>
            </Paper>

            {(detail.sections || []).map((section, si) => (
                <Box key={si} sx={{ mb: 2 }}>
                    {section.title && (
                        <Typography sx={{ fontWeight: 'bold', color: globals.colors.darkBlue, mb: 1 }}>
                            {section.title}
                        </Typography>
                    )}
                    {(section.questions || []).map((q, qi) => (
                        <Paper key={q.id} elevation={1} sx={{ p: 2, mb: 1.5 }}>
                            <Grid container spacing={1}>
                                <Grid item xs={12} md={8}>
                                    <Typography sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        {qi + 1}. {renderWithLatex(q.text)}
                                    </Typography>
                                    {q.image_url && (
                                        <Box component="img" src={q.image_url} alt=""
                                            sx={{ maxWidth: '100%', borderRadius: 1, mb: 1 }} />
                                    )}
                                    <StudentAnswer question={q} answer={(detail.answers || {})[String(q.id)]} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label={`Точки (макс. ${q.points})`}
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={grades[q.id]?.points ?? 0}
                                        onChange={e => setPoints(q.id, q.points, e.target.value)}
                                        inputProps={{ min: 0, max: q.points }}
                                        disabled={isAutoGraded(q)}
                                        helperText={isAutoGraded(q) ? 'Оценено автоматично' : ''}
                                    />
                                    <TextField
                                        label="Коментар"
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        sx={{ mt: 1 }}
                                        value={grades[q.id]?.feedback ?? ''}
                                        onChange={e => setQFeedback(q.id, e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                </Box>
            ))}

            <Divider sx={{ my: 2 }} />

            <TextField
                label="Общ коментар към ученика"
                fullWidth
                multiline
                minRows={2}
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button variant="outlined" onClick={() => save(false)} disabled={saving}
                    sx={{ color: globals.colors.darkBlue, borderColor: globals.colors.darkBlue }}>
                    Запази чернова
                </Button>
                <Button variant="contained" onClick={() => save(true)} disabled={saving}
                    sx={{ backgroundColor: globals.colors.lightRed, '&:hover': { backgroundColor: globals.colors.darkRed } }}>
                    Приключи оценяването
                </Button>
                {saving && <CircularProgress size={22} />}
                {savedMsg && <Typography sx={{ fontSize: 13, color: globals.colors.gray }}>{savedMsg}</Typography>}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Submissions list for one exam
// ---------------------------------------------------------------------------

function SubmissionsList({ exam, token, onBack, onGrade }) {
    const [submissions, setSubmissions] = useState(null);

    const load = useCallback(() => {
        fetch(`${globals.apiUrl}/exams/${exam.id}/submissions`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(setSubmissions)
            .catch(() => setSubmissions([]));
    }, [exam.id, token]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
                Назад към изпитите
            </Button>
            <Typography sx={{ fontWeight: 'bold', fontSize: 20, color: globals.colors.darkBlue, mb: 2 }}>
                {exam.title}
            </Typography>

            {submissions === null ? (
                <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
            ) : submissions.length === 0 ? (
                <Typography sx={{ color: globals.colors.gray }}>Все още няма предадени изпити.</Typography>
            ) : (
                submissions.map(sub => (
                    <Paper key={sub.submission_id} elevation={1} sx={{ p: 2, mb: 1 }}>
                        <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={12} sm={5}>
                                <Typography sx={{ fontWeight: 'bold' }}>{sub.student_name}</Typography>
                                <Typography sx={{ fontSize: 13, color: globals.colors.gray }}>{sub.student_email}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontSize: 13 }}>{formatDate(sub.submitted_at)}</Typography>
                            </Grid>
                            <Grid item xs={3} sm={2}>
                                <StatusChip status={sub.grading_status} />
                                {sub.grading_status === 'graded' && (
                                    <Typography sx={{ fontSize: 13, mt: 0.5 }}>{sub.score} т.</Typography>
                                )}
                            </Grid>
                            <Grid item xs={3} sm={2} textAlign="right">
                                <Button variant="contained" size="small"
                                    onClick={() => onGrade(sub.submission_id)}
                                    sx={{ backgroundColor: globals.colors.darkBlue }}>
                                    {sub.grading_status === 'graded' ? 'Прегледай' : 'Оцени'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                ))
            )}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Root component: exam list -> submissions -> grading
// ---------------------------------------------------------------------------

const ExamGrading = ({ exams: initialExams }) => {
    const { token } = useAuth();
    const [exams, setExams] = useState(initialExams || null);
    const [selectedExam, setSelectedExam] = useState(null);
    const [gradingId, setGradingId] = useState(null);

    const loadExams = useCallback(() => {
        fetch(`${globals.apiUrl}/exams/instructor/mine`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(data => setExams(Array.isArray(data) ? data : []))
            .catch(() => setExams([]));
    }, [token]);

    useEffect(() => {
        if (!initialExams) loadExams();
    }, [initialExams, loadExams]);

    // Grading a specific submission.
    if (gradingId) {
        return (
            <GradingView
                submissionId={gradingId}
                token={token}
                onBack={(finished) => {
                    setGradingId(null);
                    if (finished) loadExams(); // refresh counts after finalizing
                }}
            />
        );
    }

    // Submissions for a selected exam.
    if (selectedExam) {
        return (
            <SubmissionsList
                exam={selectedExam}
                token={token}
                onBack={() => setSelectedExam(null)}
                onGrade={(id) => setGradingId(id)}
            />
        );
    }

    // Exam list.
    if (exams === null) {
        return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    }
    if (exams.length === 0) {
        return <Typography sx={{ color: globals.colors.gray }}>Нямате създадени изпити.</Typography>;
    }

    return (
        <Box>
            <Typography sx={{ fontWeight: 'bold', fontSize: 20, color: globals.colors.darkBlue, mb: 2 }}>
                Оценяване на изпити
            </Typography>
            {exams.map(exam => (
                <Paper key={exam.id} elevation={1} sx={{ p: 2, mb: 1 }}>
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontWeight: 'bold' }}>{exam.title}</Typography>
                            <Typography sx={{ fontSize: 13, color: globals.colors.gray }}>
                                {exam.is_published ? 'Активен' : 'Чернова'}
                            </Typography>
                        </Grid>
                        <Grid item xs={8} sm={4}>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip size="small" label={`${exam.submitted_count} предадени`} />
                                {exam.pending_count > 0 && (
                                    <Chip size="small" color="warning" label={`${exam.pending_count} за оценяване`} />
                                )}
                                {exam.graded_count > 0 && (
                                    <Chip size="small" color="success" label={`${exam.graded_count} оценени`} />
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={4} sm={2} textAlign="right">
                            <Button variant="contained" size="small"
                                disabled={exam.submitted_count === 0}
                                onClick={() => setSelectedExam(exam)}
                                sx={{ backgroundColor: globals.colors.lightRed }}>
                                Предадени
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            ))}
        </Box>
    );
};

export default ExamGrading;
